// Dynamically determine API origin based on page domain
function getApiBase(): string {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8088/api';
  }

  if (hostname === 'pitchcount.zacx.dev') {
    return 'https://pitchcount-api.zacx.dev/api';
  }

  // Fallback to injected value or relative path
  return ((window as any).__API_URL__ || '') + '/api';
}

const API_BASE = getApiBase();

export interface ApiError {
  error: string;
}

class ApiService {
  private async request<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<T> {
    const options: RequestInit = {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(`${API_BASE}${path}`, options);

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Auth
  async createSession(idToken: string) {
    return this.request<User>('POST', '/auth/session', { id_token: idToken });
  }

  async deleteSession() {
    return this.request<void>('DELETE', '/auth/session');
  }

  // Teams
  async getTeams() {
    return this.request<Team[]>('GET', '/teams');
  }

  async createTeam(data: CreateTeamRequest) {
    return this.request<Team>('POST', '/admin/teams', data);
  }

  async updateTeam(id: string, data: CreateTeamRequest) {
    return this.request<Team>('PUT', `/admin/teams/${id}`, data);
  }

  /**
   * Sets (or clears) the canonical age class for a team. Pass `null` to
   * clear — the backend then falls back to player DOB when computing pitch
   * limits. Allowed values: "U11" | "U13" | "U15" | "U18" | "U22" | null.
   *
   * 400 → invalid age_group string (the UI controls the input so this
   * should not happen in practice; surface as a toast if it does).
   * 403 → caller is not an admin. 404 → team not found.
   */
  async updateTeamAgeGroup(teamId: string, ageGroup: string | null) {
    return this.request<Team>(
      'PUT',
      `/admin/teams/${teamId}/age-group`,
      { age_group: ageGroup }
    );
  }

  async deleteTeam(id: string) {
    return this.request<void>('DELETE', `/admin/teams/${id}`);
  }

  // Seasons
  async getSeasons() {
    return this.request<Season[]>('GET', '/seasons');
  }

  async createSeason(data: CreateSeasonRequest) {
    return this.request<Season>('POST', '/admin/seasons', data);
  }

  async updateSeason(id: string, data: CreateSeasonRequest) {
    return this.request<Season>('PUT', `/admin/seasons/${id}`, data);
  }

  async deleteSeason(id: string) {
    return this.request<void>('DELETE', `/admin/seasons/${id}`);
  }

  async getDefaultSeason(): Promise<Season | null> {
    // Returns 204 No Content when no default is set; our request helper
    // returns undefined in that case.
    const result = await this.request<Season | undefined>('GET', '/seasons/default');
    return result ?? null;
  }

  async setDefaultSeason(id: string) {
    return this.request<Season>('PUT', `/admin/seasons/${id}/set-default`);
  }

  // Players
  async getPlayers(teamId?: string, gameId?: string) {
    const params = new URLSearchParams();
    if (teamId) params.set('team_id', teamId);
    if (gameId) params.set('game_id', gameId);
    const query = params.toString();
    return this.request<Player[]>('GET', `/players${query ? `?${query}` : ''}`);
  }

  async createPlayer(data: CreatePlayerRequest) {
    return this.request<Player>('POST', '/admin/players', data);
  }

  async searchPlayers(query: string): Promise<Player[]> {
    return this.request<Player[]>('GET', `/players/search?q=${encodeURIComponent(query)}`);
  }

  async createAdHocPlayer(gameId: string, data: CreatePlayerRequest) {
    return this.request<Player>('POST', `/games/${gameId}/players`, data);
  }

  async updatePlayer(id: string, data: CreatePlayerRequest) {
    return this.request<Player>('PUT', `/admin/players/${id}`, data);
  }

  /**
   * Update only the jersey number on a player. Coach-accessible (no admin
   * role required) — backend authorizes that the caller is on the player's
   * team.
   */
  async updatePlayerJersey(id: string, jerseyNumber: number | null) {
    return this.request<Player>('PATCH', `/players/${id}/jersey`, { jersey_number: jerseyNumber });
  }

  async deletePlayer(id: string) {
    return this.request<void>('DELETE', `/admin/players/${id}`);
  }

  // Games
  async getGames(seasonId?: string, teamId?: string) {
    const params = new URLSearchParams();
    if (seasonId) params.set('season_id', seasonId);
    if (teamId) params.set('team_id', teamId);
    const query = params.toString();
    return this.request<Game[]>('GET', `/games${query ? `?${query}` : ''}`);
  }

  async getGame(id: string) {
    return this.request<Game>('GET', `/games/${id}`);
  }

  async createGame(data: CreateGameRequest) {
    return this.request<Game>('POST', '/games', data);
  }

  async updateGame(id: string, data: CreateGameRequest) {
    return this.request<Game>('PUT', `/games/${id}`, data);
  }

  async deleteGame(id: string) {
    return this.request<void>('DELETE', `/games/${id}`);
  }

  // Pitch counts
  async getGamePitches(gameId: string) {
    return this.request<PitchCount[]>('GET', `/games/${gameId}/pitches`);
  }

  async upsertPitchCount(gameId: string, data: UpsertPitchCountRequest) {
    return this.request<PitchCount>('POST', `/games/${gameId}/pitches`, data);
  }

  async updatePitchCount(id: string, count: number) {
    return this.request<PitchCount>('PUT', `/pitches/${id}`, { count });
  }

  async deletePitchCount(id: string) {
    return this.request<void>('DELETE', `/pitches/${id}`);
  }

  // Pitch events (individual event tracking)
  async addPitchEvent(gameId: string, data: AddPitchEventRequest) {
    return this.request<PitchCount>('POST', `/games/${gameId}/pitch-events`, data);
  }

  async removeLatestPitchEvent(gameId: string, playerId: string) {
    return this.request<PitchCount>('DELETE', `/games/${gameId}/pitch-events/latest?player_id=${encodeURIComponent(playerId)}`);
  }

  // Stats
  async getStatsOverview() {
    return this.request<StatsOverview>('GET', '/stats/overview');
  }

  /**
   * Action-oriented league dashboard. Public — no auth required. Powers the
   * redesigned StatsView league render: This Week summary, activity by age
   * group, most active teams, and top pitchers in the last 30 days.
   *
   * Note: this is fetched in parallel with `/stats/overview` because the
   * latter still drives Recent Games + pagination. See `loadStats()` in
   * StatsView for the coordination.
   */
  async getStatsDashboard() {
    return this.request<StatsDashboard>('GET', '/stats/dashboard');
  }

  async getTeamStats(id: string) {
    return this.request<TeamStats>('GET', `/stats/team/${id}`);
  }

  async getPlayerStats(id: string) {
    return this.request<PlayerStats>('GET', `/stats/player/${id}`);
  }

  async getSeasonStats(id: string) {
    return this.request<SeasonStats>('GET', `/stats/season/${id}`);
  }

  async getGameStats(id: string) {
    return this.request<GameStats>('GET', `/stats/game/${id}`);
  }

  /**
   * Fetch a page of games — used by the StatsView "Load more" buttons under
   * per-team Game History, browsed-team Recent Games, and the org-wide
   * Recent Games list. Pass teamId to scope to a team's home/away games
   * (with pitch totals only counting that team's pitchers); omit for the
   * league-wide list. Server caps limit at 50.
   */
  async getPaginatedGames(opts: { teamId?: string; limit?: number; offset?: number } = {}) {
    const qs = new URLSearchParams();
    if (opts.teamId) qs.set('team_id', opts.teamId);
    if (typeof opts.limit === 'number') qs.set('limit', String(opts.limit));
    if (typeof opts.offset === 'number') qs.set('offset', String(opts.offset));
    const path = qs.toString() ? `/stats/games?${qs.toString()}` : '/stats/games';
    return this.request<PaginatedGames>('GET', path);
  }

  // Admin - Pitch Editing
  async bulkUpdatePitches(gameId: string, updates: BulkPitchUpdate[]) {
    return this.request<PitchCount[]>('PUT', `/admin/games/${gameId}/pitches/bulk`, { updates });
  }

  async getGameAuditLog(gameId: string, limit = 50, offset = 0) {
    return this.request<AuditLogEntry[]>('GET', `/admin/games/${gameId}/audit-log?limit=${limit}&offset=${offset}`);
  }

  async addPlayerToGame(gameId: string, playerId: string) {
    return this.request<PitchCount>('POST', `/admin/games/${gameId}/players`, { player_id: playerId });
  }

  async removePlayerFromGame(gameId: string, playerId: string) {
    return this.request<void>('DELETE', `/admin/games/${gameId}/players/${playerId}`);
  }

  // Admin - Roster Management
  async getTeamRoster(teamId: string) {
    return this.request<Player[]>('GET', `/admin/teams/${teamId}/roster`);
  }

  async getUnassignedPlayers() {
    return this.request<Player[]>('GET', '/admin/players/unassigned');
  }

  async bulkAssignPlayers(teamId: string, playerIds: string[]) {
    return this.request<Player[]>('PUT', `/admin/teams/${teamId}/players/bulk`, { player_ids: playerIds });
  }

  // Roster Import
  async uploadRosterCSV(teamId: string, file: File, seasonId?: string): Promise<ImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    if (seasonId) formData.append('season_id', seasonId);

    const response = await fetch(`${API_BASE}/admin/teams/${teamId}/roster/import`, {
      method: 'POST',
      credentials: 'include',
      body: formData, // No Content-Type header for multipart
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async confirmRosterImport(importId: string, skipDuplicates = false) {
    return this.request<RosterImportResult>('POST', `/admin/roster/import/${importId}/confirm`, { skip_duplicates: skipDuplicates });
  }

  async cancelRosterImport(importId: string) {
    return this.request<void>('DELETE', `/admin/roster/import/${importId}`);
  }

  async getImportHistory(teamId: string) {
    return this.request<RosterImportRecord[]>('GET', `/admin/teams/${teamId}/roster/imports`);
  }

  // League Import
  async uploadLeagueXLSX(file: File): Promise<LeagueImportPreview> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/admin/league/import`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async confirmLeagueImport(importId: string, skipDuplicates = false) {
    return this.request<LeagueImportResult>('POST', `/admin/league/import/${importId}/confirm`, { skip_duplicates: skipDuplicates });
  }

  async cancelLeagueImport(importId: string) {
    return this.request<void>('DELETE', `/admin/league/import/${importId}`);
  }

  // Game Submission
  async submitGame(gameId: string) {
    return this.request<GameSubmission>('POST', `/games/${gameId}/submit`);
  }

  async getGameSubmissions(gameId: string) {
    return this.request<GameSubmission[]>('GET', `/games/${gameId}/submissions`);
  }

  async getPendingGames() {
    return this.request<GameSubmissionWithGame[]>('GET', '/admin/games/pending');
  }

  async approveGame(submissionId: string) {
    return this.request<GameSubmission>('PUT', `/admin/games/${submissionId}/approve`);
  }

  async rejectGame(submissionId: string, reason: string) {
    return this.request<GameSubmission>('PUT', `/admin/games/${submissionId}/reject`, { reason });
  }

  async getPendingReviewGames() {
    return this.request<GameSubmissionWithGame[]>('GET', '/games/pending-review');
  }

  async coachApproveGame(gameId: string, submissionId: string) {
    return this.request<GameSubmission>('PUT', `/games/${gameId}/submissions/${submissionId}/coach-approve`);
  }

  async coachRequestChanges(gameId: string, submissionId: string, message: string) {
    return this.request<GameSubmission>('PUT', `/games/${gameId}/submissions/${submissionId}/request-changes`, { message });
  }

  // Pitch Rules & Limits
  async getDefaultPitchRules() {
    return this.request<PitchRule[]>('GET', '/pitch-rules/defaults');
  }

  async getSeasonPitchRules(seasonId: string) {
    return this.request<PitchRule[]>('GET', `/seasons/${seasonId}/pitch-rules`);
  }

  async createPitchRule(data: CreatePitchRuleRequest) {
    return this.request<PitchRule>('POST', '/admin/pitch-rules', data);
  }

  async updatePitchRule(id: string, data: UpdatePitchRuleRequest) {
    return this.request<PitchRule>('PUT', `/admin/pitch-rules/${id}`, data);
  }

  async deletePitchRule(id: string) {
    return this.request<void>('DELETE', `/admin/pitch-rules/${id}`);
  }

  // Rest Rules
  async getDefaultRestRules() {
    return this.request<RestRule[]>('GET', '/rest-rules/defaults');
  }

  async getSeasonRestRules(seasonId: string) {
    return this.request<RestRule[]>('GET', `/seasons/${seasonId}/rest-rules`);
  }

  async createRestRule(data: CreateRestRuleRequest) {
    return this.request<RestRule>('POST', '/admin/rest-rules', data);
  }

  async updateRestRule(id: string, data: UpdateRestRuleRequest) {
    return this.request<RestRule>('PUT', `/admin/rest-rules/${id}`, data);
  }

  async deleteRestRule(id: string) {
    return this.request<void>('DELETE', `/admin/rest-rules/${id}`);
  }

  async getPlayerPitchStatus(playerId: string, gameId: string) {
    return this.request<PitchLimitStatus>('GET', `/games/${gameId}/players/${playerId}/pitch-status`);
  }

  async getGamePitchStatus(gameId: string) {
    return this.request<PitchLimitStatus[]>('GET', `/games/${gameId}/pitch-status`);
  }

  // Rest Status
  async getPlayerRestStatus(playerId: string, seasonId?: string, excludeGameId?: string) {
    const queryParts: string[] = [];
    if (seasonId) queryParts.push(`season_id=${seasonId}`);
    if (excludeGameId) queryParts.push(`exclude_game_id=${excludeGameId}`);
    const params = queryParts.length ? `?${queryParts.join('&')}` : '';
    return this.request<RestStatus>('GET', `/players/${playerId}/rest-status${params}`);
  }

  async getTeamPitcherAvailability(teamId: string) {
    return this.request<RestStatus[]>('GET', `/teams/${teamId}/pitcher-availability`);
  }

  // Innings
  async getGameInnings(gameId: string) {
    return this.request<InningWithPitches[]>('GET', `/games/${gameId}/innings`);
  }

  async createInning(gameId: string, data: CreateInningRequest) {
    return this.request<Inning>('POST', `/games/${gameId}/innings`, data);
  }

  async advanceInning(gameId: string) {
    return this.request<Inning>('POST', `/games/${gameId}/innings/advance`);
  }

  async endInning(gameId: string, inningId: string) {
    return this.request<Inning>('PUT', `/games/${gameId}/innings/${inningId}/end`);
  }

  /**
   * Soft-deletes an inning. Used by the InningTracker "Remove this inning"
   * affordance after a coach hits "Next" one too many times. The UI only
   * surfaces this for the latest half-inning when it has zero recorded
   * pitches, so the 409 ("inning has recorded pitches") path is a defensive
   * guard rather than an expected case.
   *
   * 403 → caller isn't a coach on this game. 404 → inning not found.
   * 409 → inning has recorded pitches (admin can still restore).
   */
  async deleteInning(gameId: string, inningId: string) {
    return this.request<void>('DELETE', `/games/${gameId}/innings/${inningId}`);
  }

  async recordInningPitch(gameId: string, inningId: string, data: { player_id: string; count: number }) {
    return this.request<unknown>('POST', `/games/${gameId}/innings/${inningId}/pitches`, data);
  }

  // Observer Mode
  async generateObserverLink(gameId: string) {
    return this.request<GenerateObserverLinkResponse>('POST', `/games/${gameId}/observer-link`);
  }

  async observeGame(token: string) {
    return this.request<ObserveGameResponse>('GET', `/games/observe?token=${encodeURIComponent(token)}`);
  }

  // Coach Invitation
  async validateInvite(token: string) {
    return this.request<InviteTokenInfo>('GET', `/invite/validate?token=${encodeURIComponent(token)}`);
  }

  async redeemInvite(token: string) {
    return this.request<User>('POST', '/invite/redeem', { token });
  }

  async getCurrentUser() {
    return this.request<User>('GET', '/me');
  }

  // Admin - Team Invitations
  async createTeamInvite(teamId: string) {
    return this.request<GenerateInviteLinkResponse>('POST', `/admin/teams/${teamId}/invite`);
  }

  async getTeamInvite(teamId: string) {
    return this.request<InviteActiveResponse>('GET', `/admin/teams/${teamId}/invite`);
  }

  async revokeTeamInvite(teamId: string) {
    return this.request<void>('DELETE', `/admin/teams/${teamId}/invite`);
  }

  async getTeamCoaches(teamId: string) {
    return this.request<CoachInfo[]>('GET', `/admin/teams/${teamId}/coaches`);
  }

  async removeTeamCoach(teamId: string, userId: string) {
    return this.request<void>('DELETE', `/admin/teams/${teamId}/coaches/${userId}`);
  }

  // Coach Email Invites
  async getStaffCoaches() {
    return this.request<StaffCoach[]>('GET', '/admin/coaches/staff');
  }

  async bulkInviteCoaches(invites: CoachInviteEntry[]) {
    return this.request<BulkInviteResponse>('POST', '/admin/coaches/bulk-invite', { invites });
  }

  /**
   * Issues a fresh invite for a staff coach whose previous invite link has
   * expired (or whose original send failed). Replaces any existing pending
   * invite for that team — the backend invalidates the old token. Returns
   * `expires_at` so the UI can reflect the new TTL without refetching the
   * full list.
   *
   * 400 → coach already joined the team (UI should refresh that row).
   * 404 → player not found / not a staff coach / has no email on record.
   */
  async resendCoachInvite(playerId: string) {
    return this.request<{ success: boolean; expires_at: string }>(
      'POST',
      `/admin/staff-coaches/${playerId}/resend-invite`
    );
  }

  /**
   * Updates a user's email address. Used by admins to fix typos or follow
   * the coach to a new address after they've already redeemed their invite.
   * Server-side, this also revokes the user's session — but that's an
   * internal detail; callers should treat success as "email saved" and
   * not surface the implicit sign-out.
   *
   * 400 → invalid email format. 404 → user not found.
   * 409 → email already in use by another user (surface inline, not as toast).
   */
  async updateUserEmail(userId: string, email: string) {
    return this.request<User>(
      'PUT',
      `/admin/users/${userId}/email`,
      { email }
    );
  }

  async getInviteHistory(teamId?: string) {
    const params = teamId ? `?team_id=${teamId}` : '';
    return this.request<InviteEmail[]>('GET', `/admin/coaches/invite-history${params}`);
  }

  async getUnassignedUsers() {
    return this.request<UnassignedUser[]>('GET', '/admin/users/unassigned');
  }

  async assignUserToTeam(userId: string, teamId: string) {
    return this.request<{ message: string }>('POST', `/admin/users/${userId}/teams`, { team_id: teamId });
  }

  // Counter-mode presence (prevent two coaches from double-counting)
  async counterHeartbeat(gameId: string, clientId: string) {
    return this.request<CounterHeartbeatResponse>(
      'POST',
      `/games/${gameId}/counter-heartbeat`,
      { client_id: clientId }
    );
  }

  async counterTakeover(gameId: string, clientId: string) {
    return this.request<void>(
      'POST',
      `/games/${gameId}/counter-takeover`,
      { client_id: clientId }
    );
  }

  async counterLeave(gameId: string, clientId: string) {
    return this.request<void>(
      'DELETE',
      `/games/${gameId}/counter-heartbeat?client_id=${encodeURIComponent(clientId)}`
    );
  }
}

export const api = new ApiService();

// Counter-mode presence types
export interface CounterPresenceEntry {
  client_id: string;
  user_id: string;
  display_name: string;
}

export interface CounterHeartbeatResponse {
  evicted: boolean;
  others: CounterPresenceEntry[];
}

// Types
export interface User {
  id: string;
  firebase_uid: string;
  email: string;
  display_name: string;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  team_ids?: string[];
  teams?: UserTeam[];
}

export interface UserTeam {
  id: string;
  user_id: string;
  team_id: string;
  team_name?: string;
  assigned_at: string;
  assigned_by?: string;
}

export interface InviteTokenInfo {
  token: string;
  team_name: string;
  team_id: string;
  expires_at: string;
  valid: boolean;
}

export interface InviteActiveResponse {
  active: boolean;
  token?: string;
  expires_at?: string;
  url?: string;
  created_at?: string;
}

export interface GenerateInviteLinkResponse {
  token: string;
  expires_at: string;
  url: string;
}

export interface CoachInfo {
  user_id: string;
  email: string;
  display_name: string;
  assigned_at: string;
}

export interface Team {
  id: string;
  name: string;
  city?: string;
  /**
   * Canonical age class for the team. Drives pitch-limit calculations
   * server-side: when set, this is the source of truth; when null, the
   * backend falls back to per-player DOB. Migration auto-populates this
   * for ~600 teams; ~170 will land as NULL and need admin attention
   * (surfaced via the audit banner in TeamManager).
   *
   * Allowed values: "U11" | "U13" | "U15" | "U18" | "U22" | null.
   */
  age_group?: string | null;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface Season {
  id: string;
  name: string;
  year: number;
  start_date?: string;
  end_date?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface Player {
  id: string;
  team_id?: string;
  game_id?: string;
  name: string;
  jersey_number?: number;
  position?: string;
  is_adhoc: boolean;
  team_name?: string;
  date_of_birth?: string;
  created_at: string;
  updated_at: string;
}

export interface Game {
  id: string;
  name: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  home_team_id?: string;
  away_team_id?: string;
  season_id?: string;
  game_date: string;
  status?: string; // in_progress, pending_approval, approved
  created_by?: string;
  home_team_name?: string;
  away_team_name?: string;
  season_name?: string;
  created_at: string;
  updated_at: string;
}

export interface PitchCount {
  id: string;
  game_id: string;
  player_id: string;
  count: number;
  player_name?: string;
  jersey_number?: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface StatsOverview {
  total_games: number;
  total_pitches: number;
  total_players: number;
  total_teams: number;
  top_players: PlayerPitchStats[];
  recent_games: GameSummary[];
  /** Total league-wide games (drives the "Load more" affordance). */
  recent_games_total: number;
  pitches_by_month: MonthlyStats[];
  pitches_by_team: TeamPitchStats[];
}

/** Response shape for GET /stats/games. */
export interface PaginatedGames {
  games: GameSummary[];
  total: number;
}

/**
 * Canonical age-group buckets surfaced by the dashboard endpoint. The
 * backend always returns all six rows (including 0-count buckets) so the
 * UI can render predictable structure regardless of activity.
 */
export type DashboardAgeGroup = 'U11' | 'U13' | 'U15' | 'U18' | 'U22' | 'Unknown';

export interface DashboardThisWeek {
  games: number;
  pitches: number;
  active_teams: number;
  active_players: number;
  /** ISO timestamp marking the start of the 7-day window. */
  since: string;
}

export interface DashboardAgeGroupRow {
  age_group: DashboardAgeGroup;
  games: number;
  pitches: number;
  players: number;
}

export interface DashboardActiveTeam {
  team_id: string;
  team_name: string;
  age_group: string | null;
  games_played: number;
  total_pitches: number;
}

export interface DashboardTopPitcher {
  player_id: string;
  player_name: string;
  team_name: string;
  total_pitches: number;
  games_played: number;
  avg_per_game: number;
}

/** Frozen contract — see api/internal/handlers (parallel backend agent). */
export interface StatsDashboard {
  this_week: DashboardThisWeek;
  by_age_group: DashboardAgeGroupRow[];
  most_active_teams: DashboardActiveTeam[];
  top_pitchers_30d: DashboardTopPitcher[];
}

export interface PlayerPitchStats {
  player_id: string;
  player_name: string;
  team_name?: string;
  total_pitches: number;
  games_played: number;
  avg_per_game: number;
}

export interface GameSummary {
  game_id: string;
  game_name: string;
  game_date: string;
  location?: string;
  total_pitches: number;
  player_count: number;
}

export interface MonthlyStats {
  month: string;
  total_pitches: number;
  game_count: number;
}

export interface TeamPitchStats {
  team_id: string;
  team_name: string;
  total_pitches: number;
  player_count: number;
}

export interface TeamStats {
  team: Team;
  total_pitches: number;
  total_games: number;
  players: PlayerPitchStats[];
  game_history: GameSummary[];
  /** Total games this team played (drives the "Load more" affordance). */
  game_history_total: number;
}

export interface PlayerStats {
  player: Player;
  total_pitches: number;
  total_games: number;
  avg_per_game: number;
  game_history: GameSummary[];
}

export interface SeasonStats {
  season: Season;
  total_pitches: number;
  total_games: number;
  top_players: PlayerPitchStats[];
  team_stats: TeamPitchStats[];
}

export interface GameStats {
  game: Game;
  total_pitches: number;
  pitch_counts: PitchCount[];
}

export interface CreateTeamRequest {
  name: string;
  city?: string;
}

export interface CreateSeasonRequest {
  name: string;
  year: number;
  start_date?: string;
  end_date?: string;
}

export interface CreatePlayerRequest {
  team_id?: string;
  game_id?: string;
  name: string;
  jersey_number?: number;
  position?: string;
  is_adhoc?: boolean;
  date_of_birth?: string;
}

export interface CreateGameRequest {
  name: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  home_team_id?: string;
  away_team_id?: string;
  season_id?: string;
  game_date: string;
}

export interface UpsertPitchCountRequest {
  player_id: string;
  count: number;
}

export interface AddPitchEventRequest {
  player_id: string;
  inning_id?: string;
}

// Admin types
export interface AuditLogEntry {
  id: string;
  game_id: string;
  player_id: string;
  admin_user_id: string;
  old_value: number;
  new_value: number;
  change_reason?: string;
  created_at: string;
  player_name?: string;
  admin_name?: string;
}

export interface BulkPitchUpdate {
  player_id: string;
  new_count: number;
  change_reason?: string;
}

// Roster Import types
export interface ImportSummary {
  total_rows: number;
  new_players: number;
  updated_players: number;
  skipped: number;
  errors: number;
}

export interface ParsedPlayer {
  name: string;
  dob?: string;
  jersey?: number;
  email?: string;
  position?: string;
  is_staff: boolean;
  is_duplicate: boolean;
  errors?: string[];
}

export interface ImportPreview {
  import_id: string;
  summary: ImportSummary;
  players: ParsedPlayer[];
}

export interface RosterImportResult {
  id: string;
  team_id: string;
  season_id?: string;
  imported_by: string;
  imported_at: string;
  file_name: string;
  player_count: number;
  status: string;
  error_message?: string;
  created_at: string;
}

export interface RosterImportRecord {
  id: string;
  team_id: string;
  season_id?: string;
  imported_by: string;
  imported_at: string;
  file_name: string;
  player_count: number;
  status: string;
  error_message?: string;
  created_at: string;
}

// Game Submission types
export interface GameSubmission {
  id: string;
  game_id: string;
  submitted_by: string;
  submitted_by_name?: string;
  submitted_by_team_id?: string;
  submitted_at: string;
  status: 'pending' | 'pending_away' | 'pending_admin' | 'approved' | 'rejected' | 'changes_requested';
  reviewed_by?: string;
  reviewed_by_name?: string;
  reviewed_at?: string;
  rejection_reason?: string;
  change_request_message?: string;
}

export interface GameSubmissionWithGame extends GameSubmission {
  game_name: string;
  game_date: string;
  location?: string;
}

// Pitch Rules types
export interface PitchRule {
  id: string;
  season_id?: string;
  age_group: string;
  max_pitches_game: number;
  warning_threshold_pct: number;
  created_at: string;
  updated_at: string;
}

export interface CreatePitchRuleRequest {
  season_id?: string;
  age_group: string;
  max_pitches_game: number;
  warning_threshold_pct: number;
}

export interface UpdatePitchRuleRequest {
  age_group: string;
  max_pitches_game: number;
  warning_threshold_pct: number;
}

// Rest Rules types
export interface RestRule {
  id: string;
  season_id?: string;
  /**
   * Age group this rule applies to (e.g. "U11", "U22").
   * null/undefined means the rule is a catch-all that applies when no
   * age-specific rule matches.
   */
  age_group?: string | null;
  pitch_min: number;
  pitch_max: number;
  rest_days_required: number;
  created_at: string;
  updated_at: string;
}

export interface CreateRestRuleRequest {
  season_id?: string;
  age_group?: string | null;
  pitch_min: number;
  pitch_max: number;
  rest_days_required: number;
}

export interface UpdateRestRuleRequest {
  age_group?: string | null;
  pitch_min: number;
  pitch_max: number;
  rest_days_required: number;
}

export interface RestStatus {
  player_id: string;
  player_name: string;
  last_pitch_count: number;
  last_game_date?: string;
  rest_days_needed: number;
  rest_days_elapsed: number;
  available_date?: string;
  status: 'available' | 'resting' | 'limited';
  total_pitches_season: number;
  season_games_pitched: number;
}

export interface RestThreshold {
  pitch_count: number;
  rest_days: number;
  label: string;
}

export interface PitchLimitStatus {
  player_id: string;
  player_name: string;
  age_group: string;
  current_count: number;
  max_pitches: number;
  remaining_pitches: number;
  percentage: number;
  warning_threshold: number;
  status: 'ok' | 'warning' | 'limit_reached';
  rest_status?: RestStatus;
  rest_thresholds?: RestThreshold[];
  next_rest_threshold?: RestThreshold;
}

// League Import types
export interface LeagueImportSummary {
  total_rows: number;
  total_teams: number;
  new_teams: number;
  existing_teams: number;
  total_players: number;
  new_players: number;
  updated_players: number;
  errors: number;
}

export interface LeagueTeamPreview {
  team_name: string;
  team_id?: string;
  is_new_team: boolean;
  division?: string;
  club?: string;
  players: ParsedPlayer[];
  summary: ImportSummary;
}

export interface LeagueImportPreview {
  import_id: string;
  file_name: string;
  season_name: string;
  season_id?: string;
  is_new_season: boolean;
  teams: LeagueTeamPreview[];
  summary: LeagueImportSummary;
}

export interface LeagueImportResult {
  teams_created: number;
  players_imported: number;
  players_updated: number;
  season_name: string;
  season_id?: string;
  coaches?: ImportedCoach[];
}

export interface ImportedCoach {
  name: string;
  email: string;
  position?: string;
  team_name: string;
  team_id: string;
}

// Coach Email Invite types
export interface StaffCoach {
  player_id: string;
  name: string;
  email: string;
  position?: string;
  team_id: string;
  team_name: string;
  invited: boolean;
  /**
   * True when a user with a matching email has redeemed an invite for
   * this team. Treat as a stronger signal than `invited` — these coaches
   * already control their team and should not receive bulk re-invites.
   */
  joined?: boolean;
  /**
   * The redeemed user's `users.id` — present only when a coach has joined
   * (i.e. there's a matching user row). Used by admin "edit email" flows;
   * coaches who haven't joined don't have a user yet, so editing their
   * email there is a no-op (the player.email is updated via re-import).
   */
  user_id?: string;
}

export interface UnassignedUser {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
  got_invite: boolean;
}

export interface CoachInviteEntry {
  team_id: string;
  coach_name: string;
  coach_email: string;
  team_name: string;
  /**
   * Optional. When the entry comes from a player record, include the
   * player.id so the backend can persist the (possibly admin-edited)
   * email back to players.email.
   */
  player_id?: string;
}

export interface CoachInviteResult {
  coach_name: string;
  coach_email: string;
  team_name: string;
  status: 'sent' | 'failed';
  error?: string;
}

export interface BulkInviteResponse {
  results: CoachInviteResult[];
  sent: number;
  failed: number;
}

export interface InviteEmail {
  id: string;
  team_id: string;
  invite_token_id?: string;
  coach_name: string;
  coach_email: string;
  status: string;
  error_message?: string;
  sent_at?: string;
  created_at: string;
}

// Inning types
export interface Inning {
  id: string;
  game_id: string;
  inning_number: number;
  half: 'top' | 'bottom';
  started_at: string;
  ended_at?: string;
  created_at: string;
  updated_at: string;
}

export interface PlayerInningPitches {
  id: string;
  player_id: string;
  player_name?: string;
  game_id: string;
  inning_id: string;
  pitch_count: number;
}

export interface InningWithPitches extends Inning {
  pitches: PlayerInningPitches[];
}

export interface CreateInningRequest {
  inning_number: number;
  half: 'top' | 'bottom';
}

// Observer types
export interface GenerateObserverLinkResponse {
  token: string;
  expires_at: string;
  url: string;
}

export interface ObserveGameResponse {
  game: Game;
  pitches: PitchCount[];
  mode: 'observer';
}
